import { useCallback, useState } from 'react';
import { useAuthStore } from '../stores/authStore';

/**
 * Main auth hook - provides auth state and actions
 */
export const useAuth = () => {
  const store = useAuthStore();

  return {
    // State
    user: store.user,
    session: store.session,
    anonymousId: store.anonymousId,
    isLoading: store.isLoading,
    isInitialized: store.isInitialized,
    isAuthenticated: store.isAuthenticated,
    email: store.email,
    fullName: store.fullName,
    avatarUrl: store.avatarUrl,
    error: store.error,

    // Actions
    initialize: store.initialize,
    signInWithEmail: store.signInWithEmail,
    signInWithGoogle: store.signInWithGoogle,
    signOut: store.signOut,
    clearError: store.clearError,
  };
};

/**
 * Hook that triggers auth prompt when needed
 * Returns a function to request auth and tracks if auth was requested
 */
export const useRequireAuth = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);

  const requireAuth = useCallback(
    (onAuthenticated?: () => void) => {
      if (isAuthenticated) {
        onAuthenticated?.();
        return true;
      }

      setShowAuthPrompt(true);
      return false;
    },
    [isAuthenticated]
  );

  const dismissAuthPrompt = useCallback(() => {
    setShowAuthPrompt(false);
  }, []);

  return {
    isAuthenticated,
    isLoading,
    showAuthPrompt,
    requireAuth,
    dismissAuthPrompt,
  };
};

/**
 * Get the auth header value for API requests
 */
export const useAuthHeader = () => {
  const { session, anonymousId } = useAuth();

  return {
    // For authenticated users, return the access token
    authToken: session?.access_token ?? null,
    // For anonymous users, return the anonymous ID
    anonymousId,
  };
};

